import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyApi from '../company/companyApi';
import { Company } from './companyType';
import HorizontalMenu from '../components/common/horizontalMenu';
import { Edit, Upload, FolderUp, X, University} from 'lucide-react';
import Breadcrumbs from '../components/common/breadcrumbs';
import { getCompanyCodeFromSessionStorage, formatAddress } from '../lib/global_function';
import PhotoApi from '../photo/photoApi';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';

const CompanyPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
  ];

  const MAX_PHOTO_UPLOADED_PER_COMPANY = 1;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [fileError, setFileError] = useState<string>('');
  const [photoCount, setPhotoCount] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [photoLink, setPhotoLink] = useState<string>('');
  const [logoPhotoId, setLogoPhotoId] = useState<string>('');

  useEffect(() => {
    updatePhotoCount();
  }, [company]);

  useEffect(() => {
    const fetchPhotoLink = async () => {
      if (company?.company_code) {
        try {
          const links = await PhotoApi.getPhotoLinks('company', 'small', company.company_code);

          if (links && links.length > 0) {
            setPhotoLink(links[0].url);
            setLogoPhotoId(links[0].id);
          }
        } catch (error) {
          // Error handling
        }
      }
    };

    fetchPhotoLink();
  }, [company, photoCount]);

  useEffect(() => {
    const fetchCompany = async () => {
      const companyCode = getCompanyCodeFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      try {
        const fetchedCompany = await CompanyApi.getCompanyByCode(companyCode);
        setCompany(fetchedCompany);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchCompany();
  }, []);

  const updatePhotoCount = async () => {
    if (company) {
      try {
        const count = await PhotoApi.getPhotoCount('company', company.company_code);
        setPhotoCount(count);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    navigate('/main/company/update', { state: { company: company } });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setFile(null);
        return;
      }

      setFileError('');
      setFile(selectedFile);
    }
  };

  const handleUpload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setErrorMessage('');
    setMessage(null);

    if (!file) {
      setErrorMessage('Please select a file to upload');
      return;
    }

    if (!company) {
      setErrorMessage('Company data not found');
      return;
    }

    if (!company.company_code) {
      setErrorMessage('Company not found');
      return;
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }

    try {
      setLoading(true);
      const currentCount = await PhotoApi.getPhotoCount('company', company.company_code);
      if (currentCount >= MAX_PHOTO_UPLOADED_PER_COMPANY) {
        setErrorMessage(`Maximum number of photos (${MAX_PHOTO_UPLOADED_PER_COMPANY}) has been reached for this project`);
        return;
      }

      await PhotoApi.upload(file, {
        company_code: parseInt(company.company_code),
        image_for_tag: 'company',
        image_size_tag: 'small'
      });

      setMessage('Photo uploaded successfully!');
      setFile(null);
      await updatePhotoCount();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error uploading photo');
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeletePhoto = async () => {
    if (!company?.company_code) return;

    try {
      setLoading(true);
      await PhotoApi.delete(logoPhotoId);
      setPhotoLink('');
      await updatePhotoCount();
      setMessage('Photo deleted successfully!');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error deleting photo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold text-foreground mb-2 flex items-center gap-2">
           <University className="size-7 text-primary" />
          Company Profile
        </h2>


        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        )}

        {message && (
          <Alert variant="success">
            {message}
          </Alert>
        )}

        {company && (
          <div>
            <form onSubmit={handleSubmit}>
              <fieldset className="border-2 border-muted rounded-2xl p-4 mt-1.5 shadow-card">
                <legend className="text-lg font-semibold text-foreground px-2">Edit Company:</legend>
                <div className="space-y-3">
                  <div>
                    <strong className="text-foreground font-semibold">Name:</strong>{' '}
                    <span className="text-foreground">{company.name}</span>
                  </div>

                  <div>
                    <strong className="text-foreground font-semibold">Line of Business:</strong>{' '}
                    <span className="text-foreground">{company.line_of_business}</span>
                  </div>

                  <div>
                    <strong className="text-foreground font-semibold">Address:</strong>{' '}
                    <span className="text-foreground">
                      {formatAddress(company.address_1, company.address_2, company.city, company.state, company.zip)}
                    </span>
                  </div>

                  <div>
                    <strong className="text-foreground font-semibold">Phone:</strong>{' '}
                    <span className="text-foreground">{company.phone}</span>
                  </div>

                  <div>
                    <strong className="text-foreground font-semibold">Email:</strong>{' '}
                    <span className="text-foreground">{company.email}</span>
                  </div>

                  <div>
                    <strong className="text-foreground font-semibold">Web URL:</strong>{' '}
                    <span className="text-foreground">{company?.web_url || 'N/A'}</span>
                  </div>

                  <Button type="submit" size="medium" variant="default" className="mt-5">
                    <Edit className="size-5" />
                    Edit
                  </Button>
                </div>
              </fieldset>
            </form>

            <fieldset className="border-2 border-muted rounded-2xl p-4 mt-2.5" shadow-card>
              <legend className="text-lg font-semibold text-foreground px-2">Upload Company Logo:</legend>

              <div className="mb-5">
                <Label className="text-base">
                  Company Logo: only {MAX_PHOTO_UPLOADED_PER_COMPANY} logo is allowed. You can delete it by clicking the X at the bottom right corner, and upload a different logo image.
                </Label>
              </div>

              {photoLink && (
                <div className="relative inline-block mb-5">
                  <img
                    src={photoLink}
                    alt="Company Logo"
                    className="h-[50px] w-auto object-contain"
                  />
                  <button
                    onClick={handleDeletePhoto}
                    className="absolute -bottom-2.5 -right-2.5 size-[30px] rounded-full bg-white border border-muted cursor-pointer flex items-center justify-center p-0 hover:bg-gray-50 transition-colors"
                    type="button"
                    aria-label="Delete logo"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              )}

              {(!photoLink || photoCount < MAX_PHOTO_UPLOADED_PER_COMPANY) && (
                <>
                  <div className="flex gap-2.5 mb-2.5 items-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      onClick={handleBrowseClick}
                      size="medium"
                      variant="default"
                      type="button"
                    >
                      <FolderUp className="size-5" />
                      Browse
                    </Button>
                    {file && <span className="text-base font-medium text-foreground">{file.name}</span>}
                  </div>

                  {fileError && (
                    <Alert variant="error" className="mb-2.5">
                      {fileError}
                    </Alert>
                  )}

                  <div className="mt-2.5">
                    <Button
                      onClick={handleUpload}
                      disabled={loading}
                      size="medium"
                      variant="default"
                      type="button"
                    >
                      <Upload className="size-5" />
                      {loading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </>
              )}
            </fieldset>
          </div>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default CompanyPage;